import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

const Tutorial = () => {
  const [step, setStep] = useState(parseInt(localStorage.getItem('tutorialStep') || '0'));
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    const handleStorageChange = () => {
      setStep(parseInt(localStorage.getItem('tutorialStep') || '0'));
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('TUTORIAL_UPDATE', handleStorageChange);
    
    // Inject keyframes for pulse animation if not exists
    if (!document.getElementById('tutorial-styles')) {
      const style = document.createElement('style');
      style.id = 'tutorial-styles';
      style.innerHTML = `
        @keyframes tutorialPulse {
          0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7); }
          70% { box-shadow: 0 0 0 15px rgba(59, 130, 246, 0); }
          100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
        }
        .tutorial-highlight {
          animation: tutorialPulse 2s infinite !important;
          border: 2px solid #3B82F6 !important;
          border-radius: 4px;
        }
      `;
      document.head.appendChild(style);
    }
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('TUTORIAL_UPDATE', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (step > 0) {
      let currentHighlightId = "";
      if (step === 1) currentHighlightId = "tutorial-home-extract-btn";
      else if (step === 2) currentHighlightId = "tutorial-upload-dropzone";
      else if (step === 3) currentHighlightId = "tutorial-processing-plans";
      else if (step === 4) currentHighlightId = "tutorial-templates";
      else if (step === 5) currentHighlightId = "tutorial-edit-modes";
      else if (step === 6) currentHighlightId = "tutorial-extract-execute-btn";
      else if (step === 7) currentHighlightId = "tutorial-importance-filters";
      else if (step === 8) currentHighlightId = "tutorial-template-save-input";

      if (currentHighlightId) {
        const handleHighlightClick = () => {
          const next = step + 1;
          if (next <= 8) {
            localStorage.setItem('tutorialStep', next.toString());
            setStep(next);
            window.dispatchEvent(new Event('TUTORIAL_UPDATE'));
          } else {
            localStorage.removeItem('tutorialStep');
            setStep(0);
            window.dispatchEvent(new Event('TUTORIAL_UPDATE'));
          }
        };

        const timer = setTimeout(() => {
          const el = document.getElementById(currentHighlightId);
          if (el) {
            el.classList.add('tutorial-highlight');
            // Adding true to the event listener ensures it fires during the capture phase,
            // so the tutorial advances BEFORE any navigation or bubbling logic happens.
            el.addEventListener('click', handleHighlightClick, true);
          }
        }, 500);
        
        return () => {
          clearTimeout(timer);
          const el = document.getElementById(currentHighlightId);
          if (el) {
            el.classList.remove('tutorial-highlight');
            el.removeEventListener('click', handleHighlightClick, true);
          }
        };
      }
    }
  }, [location.pathname, step]);

  if (step === 0) return null;

  const nextStep = () => {
    const next = step + 1;
    if (next > 8) {
      localStorage.removeItem('tutorialStep');
      setStep(0);
    } else {
      localStorage.setItem('tutorialStep', next.toString());
      setStep(next);
      window.dispatchEvent(new Event('TUTORIAL_UPDATE'));
    }
  };

  const skipTutorial = () => {
    localStorage.removeItem('tutorialStep');
    setStep(0);
    window.dispatchEvent(new Event('TUTORIAL_UPDATE'));
  };

  let message = "";
  let highlightId = "";

  if (step === 1) {
    if (location.pathname !== '/') {
      history.push('/');
      return null;
    }
    message = "ステップ 1: ホームページにある「データ抽出実行」ボタンをクリックしてください。";
    highlightId = "tutorial-home-extract-btn";
  } else if (step === 2) {
    if (location.pathname !== '/ocr-extract-view') {
      history.push('/ocr-extract-view');
      return null;
    }
    message = "ステップ 2: 対象のファイル（PDF等）をドラッグ＆ドロップしてアップロードしてください。";
    highlightId = "tutorial-upload-dropzone";
  } else if (step === 3) {
    if (location.pathname !== '/ocr-extract-view') {
      history.push('/ocr-extract-view');
      return null;
    }
    message = "ステップ 3: 処理プランを選択します。Standard（標準）、Premium（高精度）、Fast（高速）から選べます。";
    highlightId = "tutorial-processing-plans";
  } else if (step === 4) {
    if (location.pathname !== '/ocr-extract-view') {
      history.push('/ocr-extract-view');
      return null;
    }
    message = "ステップ 4: 定型フォーマットの場合は、テンプレートを適用することで、より正確な抽出が可能です。また、テンプレートに保存されたマスキング領域も自動的に適用されます。";
    highlightId = "tutorial-templates";
  } else if (step === 5) {
    if (location.pathname !== '/ocr-extract-view') {
      history.push('/ocr-extract-view');
      return null;
    }
    message = "ステップ 5: マスキング（不要な部分を隠す）や、スポットコピー（特定の部分だけを抽出）などの機能が使えます。";
    highlightId = "tutorial-edit-modes";
  } else if (step === 6) {
    if (location.pathname !== '/ocr-extract-view') {
      history.push('/ocr-extract-view');
      return null;
    }
    message = "ステップ 6: 準備ができたら「抽出を実行」ボタンをクリックしてAIに抽出を行わせます！";
    highlightId = "tutorial-extract-execute-btn";
  } else if (step === 7) {
    if (location.pathname !== '/ocr-extract-view') {
      history.push('/ocr-extract-view');
      return null;
    }
    message = "ステップ 7: 抽出結果の重要度（管理用・コア・補助用）で表示やダウンロードをフィルタリングできます。必要なデータだけを絞り込む際に便利です。";
    highlightId = "tutorial-importance-filters";
  } else if (step === 8) {
    if (location.pathname !== '/ocr-extract-view') {
      history.push('/ocr-extract-view');
      return null;
    }
    message = "ステップ 8: 抽出が完了した後、その抽出ルールやマスキングを新しいテンプレートとして保存することができます。名前を入力して「テンプレート保存」をクリックしてください。";
    highlightId = "tutorial-template-save-input";
  }

  return (
    <div className="tutorial-box" style={{
      position: 'fixed',
      bottom: '30px',
      right: '30px',
      backgroundColor: '#1E293B',
      color: '#fff',
      padding: '20px',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      zIndex: 9999,
      maxWidth: '300px',
      borderLeft: '4px solid #3B82F6'
    }}>
      <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#fff' }}>✨ チュートリアル ({step}/8)</h4>
      <p style={{ margin: '0 0 15px 0', fontSize: '14px', lineHeight: '1.4' }}>{message}</p>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={skipTutorial} style={{ background: 'transparent', border: 'none', color: '#94A3B8', cursor: 'pointer' }}>スキップ</button>
        <button onClick={nextStep} style={{ background: '#3B82F6', border: 'none', color: '#fff', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
          {step === 8 ? '完了' : '次へ'}
        </button>
      </div>
    </div>
  );
};

export default Tutorial;
